import { useState } from 'react';
import { Calendar, MapPin, Sparkles, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CCUFSA_EVENT } from '@/data/events';

export const CcufsaFeature = () => {
  const [logoError, setLogoError] = useState(false);
  return (
    <section id="ccufsa" className="py-16 md:py-24">
      <div className="container mx-auto">
        <div className="relative rounded-[2.5rem] overflow-hidden gradient-leaf shadow-pop">
          <div className="absolute inset-0 bg-noise opacity-[0.04]" />
          <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-accent/30 blur-3xl" />

          <div className="relative grid lg:grid-cols-12 gap-10 p-8 sm:p-12 lg:p-16 text-primary-foreground">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <Badge className="bg-accent text-accent-foreground hover:bg-accent rounded-full px-3 py-1">Featured event</Badge>
                <span className="text-xs uppercase tracking-[0.18em] text-primary-foreground/70">{CCUFSA_EVENT.hashtag}</span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.03] mt-5 text-balance">
                2026 CCUFSA<br/>
                <span className="italic font-light text-accent">National Conference.</span>
              </h2>
              <p className="mt-5 text-primary-foreground/85 text-base md:text-lg max-w-xl">
                We're proudly attending the Canadian College & University Food Services Association's
                flagship gathering of campus dining leaders — in the capital.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-xl">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-[0.16em] text-primary-foreground/60">Dates</div>
                    <div className="font-display text-lg font-medium mt-0.5">{CCUFSA_EVENT.date}</div>
                    <div className="text-xs text-primary-foreground/70">Conference: {CCUFSA_EVENT.conferenceDays}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-[0.16em] text-primary-foreground/60">Venue</div>
                    <div className="font-display text-lg font-medium mt-0.5">{CCUFSA_EVENT.venue}</div>
                    <div className="text-xs text-primary-foreground/70">{CCUFSA_EVENT.address}</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 px-6 h-12">
                  <a href={CCUFSA_EVENT.link} target="_blank" rel="noopener noreferrer">
                    Register on PheedLoop <ExternalLink className="ml-1.5 h-4 w-4" />
                  </a>
                </Button>
                <Button asChild variant="outline" className="rounded-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 px-6 h-12 bg-transparent">
                  <a href={CCUFSA_EVENT.ccufsaSite} target="_blank" rel="noopener noreferrer">
                    Visit CCUFSA.com
                  </a>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-primary-foreground rounded-3xl p-8 shadow-pop relative">
                <div className="text-center">
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Official conference</div>
                  <div className="mt-5 rounded-2xl bg-secondary/40 p-6 flex items-center justify-center min-h-[220px]">
                    {logoError ? (
                      <div className="font-display text-3xl text-primary text-center">
                        CCUFSA
                        <div className="text-base text-leaf font-normal mt-1">National Conference</div>
                      </div>
                    ) : (
                      <img
                        src={CCUFSA_EVENT.ccufsaLogo}
                        alt="2026 CCUFSA National Conference"
                        className="max-w-full max-h-[200px] object-contain"
                        onError={() => setLogoError(true)}
                      />
                    )}
                  </div>
                  <div className="mt-5 font-display text-2xl font-medium text-primary">{CCUFSA_EVENT.city}, {CCUFSA_EVENT.province}</div>
                  <div className="text-sm text-muted-foreground mt-1">{CCUFSA_EVENT.dateShort}</div>
                  <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-xs font-medium text-primary">
                    <Sparkles className="h-3.5 w-3.5 text-leaf" /> Visit us at the trade show
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CcufsaFeature;
